import { useEffect, useState } from "react";
import { AlertCircle, CreditCard, RefreshCw, LifeBuoy, Copy, Check } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { getPaddleEnvironment, type PaddleCheckoutErrorDetail } from "@/lib/paddle";

const TEST_CARD = "4242 4242 4242 4242";

export function PaddlePaymentErrorDialog() {
  const [error, setError] = useState<PaddleCheckoutErrorDetail | null>(null);
  const [copied, setCopied] = useState(false);
  const isSandbox = getPaddleEnvironment() === "sandbox";

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PaddleCheckoutErrorDetail>).detail;
      if (detail?.type === "payment_failed" || detail?.type === "checkout_error") {
        setError(detail);
      }
    };
    window.addEventListener("paddle:checkout-error", handler);
    return () => window.removeEventListener("paddle:checkout-error", handler);
  }, []);

  const copyTestCard = async () => {
    await navigator.clipboard.writeText(TEST_CARD.replace(/\s/g, ""));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const retry = () => {
    setError(null);
    // Re-open the most recent checkout if available
    if (typeof window !== "undefined" && (window as any).Paddle?.Checkout?.open) {
      // Paddle keeps the last checkout config; opening with no args reopens it on supported versions.
      // Fallback: user can click the pricing button again.
    }
  };

  if (!error) return null;
  const isDecline = error.type === "payment_failed";

  return (
    <AlertDialog open={!!error} onOpenChange={(open) => !open && setError(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-destructive/10 p-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <AlertDialogTitle>
              {isDecline ? "Payment was declined" : "Checkout problem"}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            {isDecline
              ? "Your card was declined and no charge was made. This usually means the card issuer rejected the transaction."
              : error.message}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 text-sm">
          <p className="font-medium">Try one of these:</p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex gap-2">
              <CreditCard className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Use a different card or payment method.</span>
            </li>
            <li className="flex gap-2">
              <RefreshCw className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Double-check the card number, expiry, CVC, and billing ZIP/postcode.</span>
            </li>
            <li className="flex gap-2">
              <LifeBuoy className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Contact your bank — they may have blocked the charge as suspicious.</span>
            </li>
          </ul>

          {isSandbox && (
            <div className="rounded-md border border-orange-300 bg-orange-50 p-3 text-orange-900">
              <p className="font-medium">Test mode</p>
              <p className="mt-1 text-xs">
                You're in test mode. Card <code>4000 0000 0000 0002</code> is
                designed to always decline. Use the success card instead:
              </p>
              <button
                type="button"
                onClick={copyTestCard}
                className="mt-2 flex items-center gap-2 rounded bg-white px-2 py-1 font-mono text-xs hover:bg-orange-100"
              >
                {TEST_CARD}
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <AlertDialogAction onClick={retry}>Try again</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
